import { useEffect, useMemo, useReducer, useState } from "react"
import { ActionIcon, Button, Flex, Group, Stack, Text, TextInput, Tooltip } from "@mantine/core"
import { DataTable, DataTableSortStatus } from "mantine-datatable"
import { IconEdit, IconPlus, IconSearch, IconTrash, IconX } from "@tabler/icons-react"
import { useDeleteVisit, useVisits } from "../hooks/use-visits"
import { MSaveVisit } from "../components/MSaveVisit"
import { PAGE_SIZES, ROLES } from "@/shared"
import { useAuth } from "@features/auth/hooks/useAuth.ts"
import { headerStore } from "@shared/state/header.state.ts"
import { openConfirmModal } from "@mantine/modals"
import { useDynamicHeight } from "@/shared/hooks/responsive.queries"
import dayjs from "dayjs"
import _ from "lodash"

interface SearchVisits {
  medico: string
  motivo: string
}

interface VisitsState {
  search: SearchVisits
  sortStatus: DataTableSortStatus<any>
  page: number
  pageSize: number
}

type VisitsAction = { type: "SET_SEARCH"; payload: Partial<SearchVisits> } | { type: "SET_SORT_STATUS"; payload: DataTableSortStatus<any> } | { type: "SET_PAGE"; payload: number } | { type: "SET_PAGE_SIZE"; payload: number }

const initialVisitsState: VisitsState = {
  search: { medico: "", motivo: "" },
  sortStatus: { columnAccessor: "fecha", direction: "desc" },
  page: 1,
  pageSize: PAGE_SIZES[1]
}

function visitsReducer(state: VisitsState, action: VisitsAction): VisitsState {
  switch (action.type) {
    case "SET_SEARCH":
      return { ...state, search: { ...state.search, ...action.payload }, page: 1 }
    case "SET_SORT_STATUS":
      return { ...state, sortStatus: action.payload }
    case "SET_PAGE":
      return { ...state, page: action.payload }
    case "SET_PAGE_SIZE":
      return { ...state, pageSize: action.payload, page: 1 }
    default:
      return state
  }
}

export default function VisitsPage() {
  const { hasRole } = useAuth()
  const { tableHeight } = useDynamicHeight()
  const [state, dispatch] = useReducer(visitsReducer, initialVisitsState)
  const { data: visits = [], isFetching, refetch } = useVisits()
  const deleteVisitMutation = useDeleteVisit()

  const [openedVisit, setOpenedVisit] = useState(false)
  const [editingVisit, setEditingVisit] = useState<any | null>(null)

  useEffect(() => {
    headerStore.setTitle("Visitas")
    headerStore.setOnRefresh(() => refetch())
    return () => {
      headerStore.setTitle(null)
      headerStore.setOnRefresh(null)
    }
  }, [refetch])

  const filteredVisits = useMemo(() => {
    let result = [...visits]
    if (state.search.medico) {
      result = result.filter((v) => v.medico?.toLowerCase().includes(state.search.medico.toLowerCase()))
    }
    if (state.search.motivo) {
      result = result.filter((v) => v.motivo?.toLowerCase().includes(state.search.motivo.toLowerCase()))
    }
    result = _.orderBy(result, [state.sortStatus.columnAccessor], [state.sortStatus.direction])
    return result
  }, [visits, state.search, state.sortStatus])

  const paginatedVisits = useMemo(() => {
    const from = (state.page - 1) * state.pageSize
    const to = from + state.pageSize
    return filteredVisits.slice(from, to)
  }, [filteredVisits, state.page, state.pageSize])

  const handleCreateVisit = () => {
    setEditingVisit(null)
    setOpenedVisit(true)
  }

  const handleEditVisit = (visit: any) => {
    setEditingVisit(visit)
    setOpenedVisit(true)
  }

  const handleDeleteVisit = (id: string) => {
    openConfirmModal({
      title: "Eliminar Visita",
      centered: true,
      children: <Text size="sm">¿Está seguro que desea eliminar esta visita? Esta acción no se puede deshacer.</Text>,
      labels: { confirm: "Eliminar", cancel: "Cancelar" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteVisitMutation.mutate(id)
    })
  }

  return (
    <Stack gap="md">
      <Flex justify="flex-end">
        {(hasRole(ROLES.ADMIN) || hasRole(ROLES.VISIT_CREATE)) && (
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreateVisit}>
            Agregar Visita
          </Button>
        )}
      </Flex>

      <DataTable
        withTableBorder
        borderRadius="md"
        striped
        highlightOnHover
        fetching={isFetching}
        records={paginatedVisits}
        totalRecords={filteredVisits.length}
        recordsPerPage={state.pageSize}
        page={state.page}
        onPageChange={(p) => dispatch({ type: "SET_PAGE", payload: p })}
        recordsPerPageOptions={PAGE_SIZES}
        onRecordsPerPageChange={(s) => dispatch({ type: "SET_PAGE_SIZE", payload: s })}
        recordsPerPageLabel="Cantidad por página"
        sortStatus={state.sortStatus}
        onSortStatusChange={(s) => dispatch({ type: "SET_SORT_STATUS", payload: s })}
        columns={[
          {
            accessor: "medico",
            title: "Médico",
            sortable: true,
            render: (v: any) => (typeof v.medico === "object" ? v.medico?.nombre : v.medico) || "-",
            filtering: state.search.medico !== "",
            filter: (
              <TextInput
                placeholder="Buscar médico"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.medico && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => dispatch({ type: "SET_SEARCH", payload: { medico: "" } })}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.medico}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { medico: e.currentTarget.value } })}
              />
            )
          },
          { accessor: "fecha", title: "Fecha", sortable: true, render: (r: any) => (r.fecha ? dayjs(r.fecha).format("DD/MM/YYYY") : "-") },
          {
            accessor: "motivo",
            title: "Motivo",
            sortable: true,
            filtering: state.search.motivo !== "",
            filter: (
              <TextInput
                placeholder="Buscar motivo"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.motivo && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => dispatch({ type: "SET_SEARCH", payload: { motivo: "" } })}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.motivo}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { motivo: e.currentTarget.value } })}
              />
            )
          },
          { accessor: "observaciones", title: "Observaciones" },
          {
            accessor: "actions",
            title: "Acciones",
            textAlign: "right",
            hidden: !hasRole(ROLES.ADMIN) && !hasRole(ROLES.VISIT_EDIT) && !hasRole(ROLES.VISIT_DELETE),
            render: (visit: any) => (
              <Group gap={4} justify="right" wrap="nowrap">
                {(hasRole(ROLES.ADMIN) || hasRole(ROLES.VISIT_EDIT)) && (
                  <Tooltip label="Editar Visita">
                    <ActionIcon size="sm" variant="subtle" color="blue.7" onClick={() => handleEditVisit(visit)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {(hasRole(ROLES.ADMIN) || hasRole(ROLES.VISIT_DELETE)) && (
                  <Tooltip label="Eliminar Visita">
                    <ActionIcon size="sm" variant="subtle" color="red.7" onClick={() => handleDeleteVisit(visit.id)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            )
          }
        ]}
        style={{ height: tableHeight }}
      />

      <MSaveVisit opened={openedVisit} onClose={() => setOpenedVisit(false)} editingVisit={editingVisit} />
    </Stack>
  )
}
